/**
 * Consent-gated analytics and advertising tags.
 *
 * Loading paths (deliberately one per vendor):
 *   - GA4 and Microsoft Clarity: loaded directly from this file.
 *   - Google Tag Manager: loaded from this file; it carries Meta Pixel,
 *     Google Ads, LinkedIn and the lead conversions (see docs/tracking-setup.md).
 *   - OpenAI pixel and Plerdy: loaded directly from this file.
 *
 * Consent is versioned. The old analytics-only choice (key `cookie-consent`)
 * does not cover advertising, so it is ignored and the visitor is asked again.
 * Google Consent Mode v2 defaults every storage type to denied before any tag
 * runs; "granted" is sent only after an explicit accept.
 */

const GA_MEASUREMENT_ID = "G-RMFBE9CHH3";
const GA_DISABLE_FLAG = `ga-disable-${GA_MEASUREMENT_ID}`;
const OAIQ_PIXEL_ID = "BWZWHJH4xT3fb4Ndwf9LTo";
const GTM_CONTAINER_ID = "GTM-NBHJDPST";
const CLARITY_PROJECT_ID = "wq51p8lwtk";

/** Current consent storage key. Bump the suffix whenever the scope changes. */
export const CONSENT_KEY = "cookie-consent-v2";
/** Analytics-only choice from before advertising was disclosed. Never trusted. */
export const LEGACY_CONSENT_KEY = "cookie-consent";

/** Form types allowed into analytics (mirrors the server allowlist). */
export const LEAD_REQUEST_TYPES = new Set([
  "strategy_call",
  "factory_assessment",
  "starter_package",
  "siri_assessment",
  "siri_consultation",
  "hardware_inquiry",
  "module_inquiry",
  "integration_inquiry",
  "demo_request",
  "contact_sales",
]);

type Gtag = (...args: unknown[]) => void;
type Fn = ((...args: unknown[]) => void) & { q?: unknown[] };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    fbq?: Fn;
  }
}

const CONSENT_DENIED = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
} as const;
const CONSENT_GRANTED = {
  analytics_storage: "granted",
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
} as const;

let defaultsSet = false;
let gaLoaded = false;
let gtmLoaded = false;
let oaiqLoaded = false;
let plerdyLoaded = false;
let clarityLoaded = false;
/** True while collection is currently allowed. */
let collecting = false;
let lastPagePath: string | null = null;

/** Test-only: resets module state between cases. */
export function __resetAnalyticsForTests() {
  defaultsSet = gaLoaded = gtmLoaded = oaiqLoaded = plerdyLoaded = clarityLoaded = false;
  collecting = false;
  lastPagePath = null;
  regionKnown = false;
  regionRequiresConsent = true;
}

/**
 * Fallback decline marker (strictly necessary, no identifier). Used when
 * localStorage fails so a withdrawal still survives a reload. A decline
 * marker always wins over a stored acceptance (fail closed).
 */
export const DECLINE_COOKIE = "bd-consent-declined";

function hasDeclineCookie(): boolean {
  try {
    return document.cookie.split(";").some((c) => c.trim() === `${DECLINE_COOKIE}=1`);
  } catch {
    return false;
  }
}

function writeDeclineCookie(on: boolean): boolean {
  try {
    document.cookie = on
      ? `${DECLINE_COOKIE}=1; path=/; max-age=31536000; SameSite=Lax`
      : `${DECLINE_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    return hasDeclineCookie() === on;
  } catch {
    return false;
  }
}

export function getStoredConsent(): "accepted" | "declined" | null {
  if (hasDeclineCookie()) return "declined";
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "accepted" || v === "declined" ? v : null;
  } catch {
    return null;
  }
}

export const hasAnalyticsConsent = () => getStoredConsent() === "accepted";

/**
 * Regions where prior consent is required before any tracking
 * (EU/EEA, United Kingdom, Switzerland). Everywhere else ordinary
 * tracking runs without a banner, honouring an explicit decline.
 */
const CONSENT_REGIONS = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", "IS", "LI", "NO", "GB", "CH",
]);

let regionKnown = false;
/** Fail closed: consent is required until a region lookup proves otherwise. */
let regionRequiresConsent = true;

/**
 * Reads the visitor's country from the same-origin Cloudflare trace.
 * Unknown (XX), Tor (T1), failures and timeouts all count as
 * consent-required. Cached for the page visit.
 */
export async function detectConsentRegion(): Promise<boolean> {
  if (regionKnown) return regionRequiresConsent;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2000);
    const res = await fetch("/cdn-cgi/trace", { signal: ctrl.signal, cache: "no-store" });
    clearTimeout(timer);
    if (!res.ok) throw new Error("trace unavailable");
    const text = await res.text();
    const loc = /^loc=(.+)$/m.exec(text)?.[1]?.trim().toUpperCase() ?? "";
    regionRequiresConsent =
      loc.length !== 2 || loc === "XX" || loc === "T1" || CONSENT_REGIONS.has(loc);
  } catch {
    regionRequiresConsent = true;
  }
  regionKnown = true;
  return regionRequiresConsent;
}

/** True when collection may run: explicit acceptance, or a region that does not require consent and no decline on record. */
const collectionAllowed = () =>
  hasAnalyticsConsent() ||
  (regionKnown && !regionRequiresConsent && getStoredConsent() !== "declined");

function ensureGtagStub() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    } as Gtag;
  }
}

function addScript(src: string, opts: { defer?: boolean; data?: Record<string, string> } = {}) {
  const s = document.createElement("script");
  if (opts.defer) s.defer = true;
  else s.async = true;
  if (opts.data) Object.entries(opts.data).forEach(([k, v]) => (s.dataset[k] = v));
  s.src = src;
  document.head.appendChild(s);
}

/** Consent Mode v2 defaults; must run before any Google tag. */
function setConsentDefaults() {
  if (defaultsSet) return;
  defaultsSet = true;
  ensureGtagStub();
  window.gtag!("consent", "default", { ...CONSENT_DENIED, wait_for_update: 500 });
}

function initGaOnce() {
  if (gaLoaded) return;
  gaLoaded = true;
  const gtag = window.gtag!;
  gtag("js", new Date());
  // Route changes are reported manually, so the tag must not send its own.
  gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
  addScript(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`);
}

/** GTM shares window.dataLayer, so the consent defaults are already queued. */
function initGtmOnce() {
  if (gtmLoaded) return;
  gtmLoaded = true;
  window.dataLayer!.push({ "gtm.start": Date.now(), event: "gtm.js" });
  addScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`);
}

function initOaiqOnce() {
  if (oaiqLoaded) return;
  oaiqLoaded = true;
  const w = window as unknown as { oaiq?: Fn };
  if (typeof w.oaiq !== "function") {
    const q = function (...args: unknown[]) { q.q!.push(args); } as Fn;
    q.q = [];
    w.oaiq = q;
  }
  w.oaiq!("init", { pixelId: OAIQ_PIXEL_ID, debug: false });
  addScript("https://bzrcdn.openai.com/sdk/oaiq.min.js");
}

/** Plerdy heatmaps + A/B testing (site 60343). */
function initPlerdyOnce() {
  if (plerdyLoaded) return;
  plerdyLoaded = true;
  const w = window as unknown as Record<string, unknown>;
  w._site_hash_code = "75eac4c390d517e22add1bb81537714b";
  w._suid = 60343;
  addScript("https://a.plerdy.com/public/js/click/main.js?v=" + Math.random(), {
    defer: true,
    data: { plerdymainscript: "plerdymainscript" },
  });
  addScript("https://a.plerdy.com/public/js/click/plerdy_ab-min.js?v=96fe72e");
}

function clarityFn(): Fn {
  const w = window as unknown as { clarity?: Fn };
  if (typeof w.clarity !== "function") {
    const c = function (...args: unknown[]) { (c.q = c.q || []).push(args); } as Fn;
    w.clarity = c;
  }
  return w.clarity!;
}

function initClarityOnce() {
  if (clarityLoaded) return;
  clarityLoaded = true;
  clarityFn();
  addScript(`https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`);
}

/** Pushes the visitor's choice to every vendor that exposes a consent API. */
function applyVendorConsent(granted: boolean) {
  ensureGtagStub();
  (window as unknown as Record<string, boolean>)[GA_DISABLE_FLAG] = !granted;
  window.gtag!("consent", "update", granted ? CONSENT_GRANTED : CONSENT_DENIED);
  // Clarity Consent API v2 (keys are case-sensitive).
  if (granted || clarityLoaded) {
    clarityFn()("consentv2", {
      ad_Storage: granted ? "granted" : "denied",
      analytics_Storage: granted ? "granted" : "denied",
    });
  }
  // Meta Pixel is loaded by GTM; only signal it when it exists.
  if (typeof window.fbq === "function") window.fbq("consent", granted ? "grant" : "revoke");
}

/** Turns collection on. Safe to call repeatedly. */
export function enableAnalytics() {
  if (typeof window === "undefined") return;
  if (!collectionAllowed()) return;
  setConsentDefaults();
  applyVendorConsent(true);

  initGaOnce();
  initGtmOnce();
  initOaiqOnce();
  initPlerdyOnce();
  initClarityOnce();

  if (collecting) return;
  collecting = true;
  lastPagePath = null;
  trackPageView(window.location.pathname + window.location.search);
}

const anyTagLoaded = () => gaLoaded || gtmLoaded || oaiqLoaded || plerdyLoaded || clarityLoaded;

/**
 * Turns collection off, updates vendor consent and clears reachable cookies.
 * Returns true when a reload is needed to unload already running SDKs.
 */
export function disableAnalytics(): boolean {
  if (typeof window === "undefined") return false;
  collecting = false;
  lastPagePath = null;
  setConsentDefaults();
  applyVendorConsent(false);
  clearTrackingCookies();
  return anyTagLoaded();
}

const TRACKING_COOKIE = /^(_ga|_ga_.+|_gcl_.+|_clck|_clsk|_fbp|_fbc)$/;

export function clearTrackingCookies() {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = new Set<string>([host, `.${host}`]);
  if (parts.length > 2) domains.add(`.${parts.slice(-2).join(".")}`);
  const expire = "expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";

  document.cookie.split(";").forEach((raw) => {
    const name = raw.split("=")[0]?.trim();
    if (!name || !TRACKING_COOKIE.test(name)) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; ${expire}; domain=${d}`;
    });
    document.cookie = `${name}=; ${expire}`;
  });
}

/**
 * Called once at app start. Loads tags only with a stored acceptance, or
 * without any banner when the visitor's region does not require consent.
 * An explicit decline always wins.
 */
export function initAnalytics() {
  if (typeof window === "undefined") return;
  setConsentDefaults();
  if (hasAnalyticsConsent()) {
    enableAnalytics();
    return;
  }
  (window as unknown as Record<string, boolean>)[GA_DISABLE_FLAG] = true;
  if (getStoredConsent() === "declined") return;
  void detectConsentRegion().then((required) => {
    if (!required && getStoredConsent() !== "declined") enableAnalytics();
  });
}

/**
 * Records the visitor's choice and applies it immediately. After a decline
 * that follows loaded tags, the page reloads (same URL) so no third-party SDK
 * keeps running — but only if the decline was actually persisted, otherwise
 * the reload would load the banner again in a loop.
 */
export function setAnalyticsConsent(
  choice: "accepted" | "declined",
  reload: () => void = () => window.location.reload(),
) {
  let persisted = false;
  try {
    localStorage.setItem(CONSENT_KEY, choice);
    localStorage.removeItem(LEGACY_CONSENT_KEY);
    persisted = localStorage.getItem(CONSENT_KEY) === choice;
  } catch {
    /* storage unavailable — handled below */
  }
  if (choice === "accepted") {
    if (persisted && writeDeclineCookie(false)) enableAnalytics();
    return;
  }
  // Decline: the cookie marker makes the withdrawal survive a reload even
  // when localStorage throws, and overrides any stale stored acceptance.
  const cookieSaved = writeDeclineCookie(true);
  const needsReload = disableAnalytics();
  if (!needsReload) return;
  if (persisted || cookieSaved) {
    reload();
  } else {
    // Nothing could be persisted: a reload would reload the trackers, so
    // neutralise the running SDKs in place instead (fail closed, no loop).
    neutraliseLoadedSdks();
  }
}

/** Best-effort in-page stop when the decline cannot be persisted. */
function neutraliseLoadedSdks() {
  const w = window as unknown as Record<string, unknown>;
  try {
    if (typeof w.clarity === "function") (w.clarity as Fn)("stop");
  } catch { /* noop */ }
  const noop = () => undefined;
  w.oaiq = noop;
  w.fbq = noop;
  w.gtag = noop;
  w.dataLayer = [];
  document
    .querySelectorAll?.('script[src*="plerdy"],script[src*="oaiq"],script[src*="clarity.ms"]')
    .forEach((el) => el.remove());
}

export function trackPageView(path: string) {
  if (!collecting || !collectionAllowed()) return;
  // Query strings can carry personal data (e.g. ?email=), so only the path is sent.
  const cleanPath = path.split("?")[0].split("#")[0];
  if (cleanPath === lastPagePath) return;
  lastPagePath = cleanPath;
  window.gtag?.("event", "page_view", {
    page_path: cleanPath,
    page_location: window.location.origin + window.location.pathname,
    page_title: document.title,
  });
}

export function track(event: string, payload: Record<string, unknown> = {}) {
  try {
    if (!collecting || !collectionAllowed()) return;
    window.gtag?.("event", event, payload);
  } catch {
    /* noop */
  }
}

/**
 * The single lead conversion signal for GTM. Sent only for a confirmed,
 * newly stored lead, only with consent, and only with the form type.
 * Never queued: if consent is absent the event is dropped.
 */
export function trackLeadSubmit(requestType: string) {
  try {
    if (!collecting || !collectionAllowed()) return;
    if (!LEAD_REQUEST_TYPES.has(requestType)) return;
    window.dataLayer?.push({ event: "lead_submit", request_type: requestType });
  } catch {
    /* analytics must never break lead submission */
  }
}
